import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Typography,
  FormControl,
  Snackbar,
  Alert,
  Modal,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Divider,
} from "@mui/material";
import {
  DirectionsBus,
  ConfirmationNumber,
  EventSeat,
  LocalGasStation,
  AccountBox,
  Business,
  CalendarToday,
  CheckCircle,
  Save,
  Refresh,
  Cancel,
} from "@mui/icons-material";

const EditVehicle = () => {
  const location = useLocation();
  const {
    vehicle,
    vehicleType,
    driverList,
    onSaveError,
    onCancel,
    encodedFormId,
  } = location.state || {};
  const navigate = useNavigate();

  // Form state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [registrationNo, setRegistrationNo] = useState("");
  const [selectedVehicleType, setSelectedVehicleType] = useState("");
  const [make, setMake] = useState("");
  const [makeNo, setMakeNo] = useState("");
  const [model, setModel] = useState("");
  const [reading, setReading] = useState("");
  const [engineNo, setEngineNo] = useState("");
  const [seatingCapacity, setSeatingCapacity] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [chassesNo, setChassesNo] = useState("");
  const [rentCharges, setRentCharges] = useState("");
  const [transporterName, setTransporterName] = useState("");
  const [transporterPhone, setTransporterPhone] = useState("");
  const [transporterAddress, setTransporterAddress] = useState("");
  const [insCompany, setInsCompany] = useState("");
  const [insAgent, setInsAgent] = useState("");
  const [agentPhone, setAgentPhone] = useState("");
  const [amountInsured, setAmountInsured] = useState("");
  const [permitNo, setPermitNo] = useState("");
  const [permitType, setPermitType] = useState("");
  const [selectedDriver, setSelectedDriver] = useState("");
  const [dateOfManufacturing, setDateOfManufacturing] = useState("");
  const [dateInsured, setDateInsured] = useState("");
  const [insuranceDueOn, setInsuranceDueOn] = useState("");
  const [permitDate, setPermitDate] = useState("");
  const [policyExpDate, setPolicyExpDate] = useState("");
  const [pollExpDate, setPollExpDate] = useState("");
  const [isActive, setIsActive] = useState(true);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Helper function to format date for input
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";

    // Regular expression to match "MM/DD/YYYY h:mm:ss A" format
    const dateRegex =
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s(\d{1,2}):(\d{2}):(\d{2})\s(AM|PM)$/i;
    const match = dateString.match(dateRegex);

    if (!match) {
      // Fallback for invalid or unexpected formats
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
    }

    let [, month, day, year, hours, minutes, seconds, period] = match;

    // Convert to 24-hour format
    hours = parseInt(hours, 10);
    if (period.toUpperCase() === "PM" && hours !== 12) {
      hours += 12;
    } else if (period.toUpperCase() === "AM" && hours === 12) {
      hours = 0;
    }

    // Pad single-digit month and day
    month = month.padStart(2, "0");
    day = day.padStart(2, "0");
    hours = hours.toString().padStart(2, "0");
    minutes = minutes.padStart(2, "0");
    seconds = seconds.padStart(2, "0");

    // Create ISO string in UTC to avoid timezone shift
    const isoString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
    const date = new Date(isoString);

    if (isNaN(date.getTime())) return "";

    // Return YYYY-MM-DD format
    return date.toISOString().split("T")[0];
  };

  // Load vehicle data when component mounts or vehicle changes
  useEffect(() => {
    if (vehicle) {
      setVehicleNumber(vehicle.vechileNo || "");
      setRegistrationNo(vehicle.registrationNo || "");
      setSelectedVehicleType(
        vehicle.vechileTypeId || vehicle.vehicleTypeId || ""
      );
      setMake(vehicle.make || "");
      setMakeNo(vehicle.makeNo || "");
      setModel(vehicle.model || "");
      setReading(vehicle.reading || "");
      setEngineNo(vehicle.engineNo || "");
      setSeatingCapacity(vehicle.seatingCapicity || "");
      setFuelType(vehicle.fuelType || "");
      setChassesNo(vehicle.chassesNo || "");
      setRentCharges(vehicle.rentCharges || "");
      setTransporterName(vehicle.transporterName || "");
      setTransporterPhone(vehicle.transporterPhone || "");
      setTransporterAddress(vehicle.transporterAdress || "");
      setInsCompany(vehicle.insCompany || "");
      setInsAgent(vehicle.insAgent || "");
      setAgentPhone(vehicle.agentphone || "");
      setAmountInsured(vehicle.amountInsured || "");
      setPermitNo(vehicle.permitNo || "");
      setPermitType(vehicle.permitType || "");
      setDateOfManufacturing(formatDateForInput(vehicle.dateOfMfg));
      setDateInsured(formatDateForInput(vehicle.dateInsured));
      setInsuranceDueOn(formatDateForInput(vehicle.insDueOn));
      setPermitDate(formatDateForInput(vehicle.permitDate));
      setPolicyExpDate(formatDateForInput(vehicle.policyExpdate));
      setPollExpDate(formatDateForInput(vehicle.pollExpdate));
      setIsActive(Boolean(vehicle.vechileOn));
    }
  }, [vehicle]);

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleSave = async () => {
    // Helper to show error and exit early
    const stop = (msg) => {
      showSnackbar(msg);
      if (typeof onSaveError === "function") {
        onSaveError(msg);
      } else {
        console.warn("onSaveError is not a function or is undefined");
      }
      return;
    };

    // Required string fields
    if (!String(vehicleNumber).trim())
      return stop("Vehicle number is required.");
    if (!String(registrationNo).trim())
      return stop("Registration number is required.");
    if (!selectedVehicleType) return stop("Vehicle type is required.");
    if (!String(make).trim()) return stop("Make is required.");
    if (!String(makeNo).trim()) return stop("Make number is required.");
    if (!String(engineNo).trim()) return stop("Engine number is required.");
    if (!String(chassesNo).trim()) return stop("Chassis number is required.");
    if (!String(transporterName).trim())
      return stop("Transporter name is required.");
    if (!String(transporterPhone).trim())
      return stop("Transporter phone is required.");
    if (!String(insCompany).trim())
      return stop("Insurance company is required.");
    if (!String(insAgent).trim()) return stop("Insurance agent is required.");
    if (!String(agentPhone).trim()) return stop("Agent phone is required.");
    if (!String(permitNo).trim()) return stop("Permit number is required.");
    if (!String(permitType).trim()) return stop("Permit type is required.");
    if (!dateOfManufacturing) return stop("Date of manufacturing is required.");
    if (!dateInsured) return stop("Insurance date is required.");
    if (!insuranceDueOn) return stop("Insurance due date is required.");
    if (!permitDate) return stop("Permit date is required.");
    if (!policyExpDate) return stop("Policy expiration date is required.");
    if (!pollExpDate) return stop("Pollution expiration date is required.");

    // Validate numeric values
    const parsedSeating = parseInt(seatingCapacity);
    const parsedRent = parseFloat(rentCharges);
    const parsedAmount = parseFloat(amountInsured);

    if (!parsedSeating || parsedSeating <= 0)
      return stop("Seating capacity must be a positive number.");
    if (parsedRent < 0) return stop("Rent charges cannot be negative.");
    if (parsedAmount < 0) return stop("Amount insured cannot be negative.");

    // Prepare data
    const vehicleData = {
      registrationNo: registrationNo,
      vechileNo: vehicleNumber,
      make: make || "DEFAULT_MAKE",
      makeNo: makeNo || "DEFAULT_MAKE",
      model: model || "", // Send empty string if model is not provided
      reading: reading || "0",
      engineNo: engineNo || "DEFAULT_ENGINE",
      dateOfMfg: dateOfManufacturing,
      seatingCapicity: parsedSeating,
      fuelType: fuelType || "Petrol",
      chassesNo: chassesNo || "CHASSIS_DEFAULT",
      rentCharges: parsedRent || 0,
      transporterName: transporterName || "DEFAULT_TRANSPORTER",
      transporterPhone: transporterPhone || "0000000000",
      transporterAdress: transporterAddress || "N/A",
      insCompany: insCompany || "DEFAULT_INSURANCE",
      insAgent: insAgent || "DEFAULT_AGENT",
      agentphone: agentPhone || "0000000000",
      amountInsured: parsedAmount || 0,
      dateInsured: dateInsured,
      insDueOn: insuranceDueOn,
      permitNo: permitNo || "N/A",
      permitType: permitType || "ANNUAL",
      permitDate: permitDate,
      policyExpdate: policyExpDate,
      pollExpdate: pollExpDate,
      vechileOn: isActive,
      vechileTypeId: selectedVehicleType,
    };

    // API Call
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Transportation/VehicleMaster/Id?Id=${vehicle.vechileID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify(vehicleData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        const errorMessage =
          result?.msg || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      setShowSuccessModal(true);
      showSnackbar("Vehicle updated successfully!", "success");
    } catch (error) {
      console.error("Error saving vehicle:", error);
      showSnackbar(error.message || "Failed to save vehicle.", "error");
    }
  };

  const resetForm = () => {
    setVehicleNumber("");
    setRegistrationNo("");
    setSelectedVehicleType("");
    setMake("");
    setMakeNo("");
    setModel("");
    setReading("");
    setEngineNo("");
    setSeatingCapacity("");
    setFuelType("");
    setChassesNo("");
    setRentCharges("");
    setTransporterName("");
    setTransporterPhone("");
    setTransporterAddress("");
    setInsCompany("");
    setInsAgent("");
    setAgentPhone("");
    setAmountInsured("");
    setPermitNo("");
    setPermitType("");
    setDateOfManufacturing("");
    setDateInsured("");
    setInsuranceDueOn("");
    setPermitDate("");
    setPolicyExpDate("");
    setPollExpDate("");
    setIsActive(false);
  };

  if (!vehicle) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper
          elevation={3}
          sx={{ p: 4, borderRadius: "8px", backgroundColor: "#fafafa" }}
        >
          <Typography variant="h5" align="center">
            No vehicle selected for editing
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Cancel />}
              onClick={() => navigate(-1)}
              sx={{ px: 4, py: 1.5, textTransform: "none" }}
            >
              Back to List
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          backgroundColor: "#0B1F40",
          padding: "16px 24px",
          boxShadow: 3,
          borderRadius: "8px 8px 0 0",
          marginBottom: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <DirectionsBus sx={{ color: "#fff", fontSize: 28 }} />
        <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
          Edit School Vehicle
        </Typography>
      </Box>

      <Paper
        elevation={3}
        sx={{ p: 4, borderRadius: "0 0 8px 8px", backgroundColor: "#fafafa" }}
      >
        <Grid container spacing={3}>
          {/* Vehicle Details Section */}
          <Grid item xs={12}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                mb: 2,
                color: "#0B1F40",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <DirectionsBus sx={{ color: "#0B1F40" }} /> Vehicle Details
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ConfirmationNumber sx={{ color: "#0B1F40" }} /> Registration
              Number <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Registration Number"
              value={registrationNo}
              onChange={(e) => setRegistrationNo(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <DirectionsBus sx={{ color: "#0B1F40" }} /> Vehicle Number{" "}
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Vehicle Number"
              value={vehicleNumber}
              onChange={(e) => setVehicleNumber(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Vehicle Type <span style={{ color: "red" }}>*</span>
            </Typography>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="vehicle-type-label">
                Select Vehicle Type
              </InputLabel>
              <Select
                labelId="vehicle-type-label"
                value={selectedVehicleType}
                onChange={(e) => setSelectedVehicleType(e.target.value)}
                label="Select Vehicle Type"
                sx={{ backgroundColor: "#fff" }}
              >
                <MenuItem value="" disabled>
                  --Select Vehicle Type--
                </MenuItem>
                {vehicleType.map((type) => (
                  <MenuItem key={type.vehicleTypeId} value={type.vehicleTypeId}>
                    {type.vehicleType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Make
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Make"
              value={make}
              onChange={(e) => setMake(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Make Number
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Make Number"
              value={makeNo}
              onChange={(e) => setMakeNo(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Model
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Reading
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Reading"
              value={reading}
              onChange={(e) => setReading(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Engine Number
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Engine Number"
              value={engineNo}
              onChange={(e) => setEngineNo(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <EventSeat sx={{ color: "#0B1F40" }} /> Seating Capacity{" "}
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Seating Capacity"
              type="number"
              value={seatingCapacity}
              onChange={(e) => setSeatingCapacity(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <LocalGasStation sx={{ color: "#0B1F40" }} /> Fuel Type
            </Typography>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="fuel-type-label">Select Fuel Type</InputLabel>
              <Select
                labelId="fuel-type-label"
                value={fuelType}
                onChange={(e) => setFuelType(e.target.value)}
                label="Select Fuel Type"
                sx={{ backgroundColor: "#fff" }}
              >
                <MenuItem value="">--Select Fuel Type--</MenuItem>
                <MenuItem value="Petrol">Petrol</MenuItem>
                <MenuItem value="Diesel">Diesel</MenuItem>
                <MenuItem value="CNG">CNG</MenuItem>
                <MenuItem value="Electric">Electric</MenuItem>
                <MenuItem value="Hybrid">Hybrid</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Chassis Number
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Chassis Number"
              value={chassesNo}
              onChange={(e) => setChassesNo(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Rent Charges
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Rent Charges"
              type="number"
              value={rentCharges}
              onChange={(e) => setRentCharges(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>

          {/* Transporter Information Section */}
          <Grid item xs={12}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                mt: 4,
                mb: 2,
                color: "#0B1F40",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <AccountBox sx={{ color: "#0B1F40" }} /> Transporter Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Transporter Name
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Transporter Name"
              value={transporterName}
              onChange={(e) => setTransporterName(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Transporter Phone
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Transporter Phone"
              inputProps={{ maxLength: "10" }}
              value={transporterPhone}
              onChange={(e) => setTransporterPhone(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Transporter Address
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Transporter Address"
              multiline
              rows={2}
              value={transporterAddress}
              onChange={(e) => setTransporterAddress(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>

          {/* Insurance Details Section */}
          <Grid item xs={12}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                mt: 4,
                mb: 2,
                color: "#0B1F40",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Business sx={{ color: "#0B1F40" }} /> Insurance Details
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Insurance Company
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Insurance Company"
              value={insCompany}
              onChange={(e) => setInsCompany(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Insurance Agent
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Insurance Agent"
              value={insAgent}
              onChange={(e) => setInsAgent(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Agent Phone
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Agent Phone"
              inputProps={{ maxLength: "10" }}
              value={agentPhone}
              onChange={(e) => setAgentPhone(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Amount Insured
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Amount Insured"
              type="number"
              value={amountInsured}
              onChange={(e) => setAmountInsured(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CalendarToday sx={{ color: "#0B1F40" }} /> Date Insured
            </Typography>
            <TextField
              type="date"
              fullWidth
              variant="outlined"
              value={dateInsured}
              onChange={(e) => setDateInsured(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CalendarToday sx={{ color: "#0B1F40" }} /> Insurance Due On
            </Typography>
            <TextField
              type="date"
              fullWidth
              variant="outlined"
              value={insuranceDueOn}
              onChange={(e) => setInsuranceDueOn(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>

          {/* Permit Details Section */}
          <Grid item xs={12}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                mt: 4,
                mb: 2,
                color: "#0B1F40",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ConfirmationNumber sx={{ color: "#0B1F40" }} /> Permit Details
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ConfirmationNumber sx={{ color: "#0B1F40" }} /> Permit Number
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Permit Number"
              value={permitNo}
              onChange={(e) => setPermitNo(e.target.value)}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              Permit Type
            </Typography>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="permit-type-label">Select Permit Type</InputLabel>
              <Select
                labelId="permit-type-label"
                value={permitType}
                onChange={(e) => setPermitType(e.target.value)}
                label="Select Permit Type"
                sx={{ backgroundColor: "#fff" }}
              >
                <MenuItem value="">--Select Permit Type--</MenuItem>
                <MenuItem value="ANNUAL">Annual</MenuItem>
                <MenuItem value="TEMPORARY">Temporary</MenuItem>
                <MenuItem value="PERMANENT">Permanent</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CalendarToday sx={{ color: "#0B1F40" }} /> Permit Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              variant="outlined"
              value={permitDate}
              onChange={(e) => setPermitDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CalendarToday sx={{ color: "#0B1F40" }} /> Policy Expiry Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              variant="outlined"
              value={policyExpDate}
              onChange={(e) => setPolicyExpDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CalendarToday sx={{ color: "#0B1F40" }} /> Pollution Expiry Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              variant="outlined"
              value={pollExpDate}
              onChange={(e) => setPollExpDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CalendarToday sx={{ color: "#0B1F40" }} /> Date of Manufacturing
            </Typography>
            <TextField
              type="date"
              fullWidth
              variant="outlined"
              value={dateOfManufacturing}
              onChange={(e) => setDateOfManufacturing(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  color="primary"
                  icon={<CheckCircle />}
                  checkedIcon={<CheckCircle sx={{ color: "#0B1F40" }} />}
                />
              }
              label="Is Active"
              sx={{ mt: 2 }}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 4,
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                onClick={handleSave}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: "bold",
                  textTransform: "none",
                }}
              >
                Update
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<Refresh />}
                onClick={resetForm}
                sx={{ px: 4, py: 1.5, textTransform: "none" }}
              >
                Reset
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={() => navigate(-1)}
                sx={{ px: 4, py: 1.5, textTransform: "none" }}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#0B1F40",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CheckCircle sx={{ color: "#fff" }} />
            <Typography
              id="success-modal-title"
              variant="h6"
              sx={{ color: "#fff", fontWeight: "bold" }}
            >
              Vehicle Updated
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography id="success-modal-description" sx={{ mb: 3 }}>
              The vehicle has been updated successfully.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                color="primary"
                startIcon={<CheckCircle />}
                sx={{ px: 4, textTransform: "none" }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditVehicle;
